import type {
  AllergenDeclaration,
  AllergenLevel,
  DayOfWeek,
  MenuItem,
  Orderability,
  RestaurantSite,
  UnorderableCode,
} from '@/src/lib/hospitality/wire';

/**
 * Everything the menu block decides, with no React and no `fetch` in sight.
 *
 * <p>Split out for one reason: these are the rules a diner with a nut allergy
 * depends on, and a rule that can only be exercised by rendering a component is
 * a rule nobody tests. `menuModel.test.ts` runs every branch below.
 *
 * <p><strong>Nothing here computes availability.</strong> `orderable` and
 * `unorderableCode` come from the server, which owns the channel's timezone and
 * the window rows; this file only chooses which sentence to print for a verdict
 * it was handed. The two functions that do touch a timezone —
 * {@link zonedInstant} and {@link timeInZone} — turn a wall-clock time into an
 * instant to ASK the server about, and format an instant the server sent. They
 * never decide whether a dish is on.
 */

/* --------------------------------------------------------------- allergens */

/**
 * The fourteen regulated allergens, in the order the regulation lists them —
 * the same order and the same keys as `AllergenCatalogue.ALL` on the server.
 *
 * <p>The backend already sends all fourteen for every dish ("All fourteen,
 * always. An absent entry would be read as 'safe' by a client."). This list is
 * the client's half of that contract, not a duplicate of it: it is what makes
 * {@link allergenTable} able to print fourteen rows even when a response is
 * short, and what makes a missing row read "not declared" rather than vanish.
 */
export const ALLERGEN_KEYS: readonly string[] = [
  'gluten',
  'crustaceans',
  'eggs',
  'fish',
  'peanuts',
  'soybeans',
  'milk',
  'nuts',
  'celery',
  'mustard',
  'sesame',
  'sulphites',
  'lupin',
  'molluscs',
] as const;

/**
 * Exactly fourteen declarations, in regulation order, whatever arrived.
 *
 * <p>A key the response did not mention becomes `UNKNOWN` — "not declared,
 * please ask" — and never `FREE_FROM`. An unrecognised key the backend DOES
 * send is appended after the fourteen rather than dropped: a tenant that has
 * added a fifteenth declaration is telling a diner something, and silence is
 * the one answer that can hurt them.
 */
export function allergenTable(declared: readonly AllergenDeclaration[]): AllergenDeclaration[] {
  const byKey = new Map<string, AllergenDeclaration>();
  for (const row of declared) {
    if (row?.allergenKey) byKey.set(row.allergenKey, row);
  }
  const table: AllergenDeclaration[] = ALLERGEN_KEYS.map(
    (key) =>
      byKey.get(key) ?? {
        allergenKey: key,
        // No label to fall back on: the caller prints the localised caption for
        // the key, and `label` is only the backend's own wording when it sent
        // a row at all.
        label: '',
        level: 'UNKNOWN' as AllergenLevel,
        source: null,
      },
  );
  for (const row of declared) {
    if (row?.allergenKey && !ALLERGEN_KEYS.includes(row.allergenKey)) table.push(row);
  }
  return table;
}

export interface AllergenSummary {
  /** Declared present. */
  contains: AllergenDeclaration[];
  /** The roll-up's cross-contamination answer — "may contain". */
  mayContain: AllergenDeclaration[];
  /** Nobody has said. Rendered as a warning, never as an absence. */
  undeclared: AllergenDeclaration[];
}

/**
 * The three lists a badge row shows, from the full fourteen.
 *
 * <p>`FREE_FROM` is deliberately absent: a badge saying "free from mustard" on
 * every dish that never contained any is noise, and the diner who needs it
 * reads the full table. The other three all get said out loud, `UNKNOWN`
 * included — the difference between "we checked and it is free from this" and
 * "nobody has said" is the whole point of the level existing.
 */
export function allergenSummary(declared: readonly AllergenDeclaration[]): AllergenSummary {
  const table = allergenTable(declared);
  return {
    contains: table.filter((row) => row.level === 'CONTAINS'),
    mayContain: table.filter((row) => row.level === 'MAY_CONTAIN'),
    undeclared: table.filter((row) => row.level === 'UNKNOWN'),
  };
}

/* ------------------------------------------------------------ availability */

export interface UnorderableReason {
  /** Message key in `src/locales/*.json`. */
  key: string;
  params?: Record<string, string>;
}

/**
 * The sentence that goes under a dish the kitchen will not cook right now.
 *
 * <p>The codes are `HospitalityErrors` constants and the wording is brief
 * 11.10a's. An unrecognised code — a backend this build has not caught up with
 * — falls through to a neutral "not available right now" and the item still
 * renders: an item is never hidden and never silently left green.
 *
 * <p>`MENU_OUTSIDE_WINDOW` reads "served from HH:mm" rather than the brief's
 * "Served 12:00–15:00". The closing time is not on the wire: `MenuItemDto`
 * carries `nextAvailableAt` and no window list, and inventing the end of the
 * service from `RestaurantSiteDto.hours` would be exactly the browser-side
 * availability computation the brief forbids. See `README.menu.md`.
 */
export function unorderableReason(
  code: UnorderableCode | null,
  nextAvailableAt: string | null,
  timeZone: string,
  locale: string,
): UnorderableReason {
  const at = timeInZone(nextAvailableAt, timeZone, locale);
  switch (code) {
    case 'MENU_ITEM_86ED':
      return at
        ? { key: 'hospitality.menu.reason.soldOutUntil', params: { time: at } }
        : { key: 'hospitality.menu.reason.soldOut' };
    case 'MENU_OUTSIDE_WINDOW':
      return at
        ? { key: 'hospitality.menu.reason.servedFrom', params: { time: at } }
        : { key: 'hospitality.menu.reason.notServedNow' };
    case 'MENU_PAST_CUTOFF':
      return { key: 'hospitality.menu.reason.kitchenClosed' };
    case 'MENU_ITEM_NOT_SELLABLE':
    case 'MENU_ITEM_NOT_LISTED':
      return { key: 'hospitality.menu.reason.notAvailable' };
    default:
      return { key: 'hospitality.menu.reason.unavailable' };
  }
}

/**
 * The menu body's verdict for a dish, overlaid with a fresher live one.
 *
 * <p>The menu body may be up to a minute old; `orderable()` is read uncached.
 * When both have spoken the live answer wins in BOTH directions — a dish that
 * has just been 86'd greys out, and one the kitchen has just put back goes
 * green — because the live read is the one that asked the server most recently.
 */
export function effectiveOrderability(item: MenuItem, live?: Orderability | null) {
  if (!live) {
    return {
      orderable: item.orderable,
      code: item.unorderableCode,
      nextAvailableAt: item.nextAvailableAt,
    };
  }
  return {
    orderable: live.orderable,
    code: live.orderable ? null : (live.code ?? item.unorderableCode),
    nextAvailableAt: live.nextAvailableAt ?? item.nextAvailableAt,
  };
}

/* ----------------------------------------------------------------- the zone */

const WEEK: readonly DayOfWeek[] = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

/** The parts of an instant as read in one IANA zone. Returns null for an
 *  unknown zone rather than silently falling back to the visitor's own, which
 *  would show an Istanbul restaurant its London hours. */
function partsInZone(instant: Date, timeZone: string) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    }).formatToParts(instant);
    const read = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    const hour = Number(read('hour')) % 24;
    return {
      year: Number(read('year')),
      month: Number(read('month')),
      day: Number(read('day')),
      hour,
      minute: Number(read('minute')),
      second: Number(read('second')),
    };
  } catch {
    return null;
  }
}

/** How far ahead of UTC `timeZone` is at `instant`, in minutes. */
function offsetMinutes(instant: Date, timeZone: string): number | null {
  const parts = partsInZone(instant, timeZone);
  if (!parts) return null;
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return (asUtc - instant.getTime()) / 60_000;
}

/** Today's date in the channel's zone, `YYYY-MM-DD`. Null for an unknown zone. */
export function todayInZone(timeZone: string, now: Date = new Date()): string | null {
  const parts = partsInZone(now, timeZone);
  if (!parts) return null;
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

/** Which weekday it is in the channel's zone. */
export function dayInZone(timeZone: string, now: Date = new Date()): DayOfWeek | null {
  const parts = partsInZone(now, timeZone);
  if (!parts) return null;
  return WEEK[new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()] ?? null;
}

/**
 * A wall-clock time in the channel's zone, as an ISO instant.
 *
 * <p>Two passes, because the offset the first pass reads is the offset at the
 * WRONG moment on a clock-change day. The second pass reads it at the candidate
 * instant and corrects. This is how "19:30 in Europe/Istanbul" becomes
 * something `Instant.parse` accepts, and it is only ever used to ask the server
 * a question — never to answer one.
 */
export function zonedInstant(
  date: string,
  time: string,
  timeZone: string,
): string | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(time.trim());
  if (!match || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const naive = Date.parse(`${date}T${match[1].padStart(2, '0')}:${match[2]}:00Z`);
  if (Number.isNaN(naive)) return null;
  const first = offsetMinutes(new Date(naive), timeZone);
  if (first == null) return null;
  let candidate = naive - first * 60_000;
  const second = offsetMinutes(new Date(candidate), timeZone);
  if (second != null) candidate = naive - second * 60_000;
  return new Date(candidate).toISOString();
}

/**
 * An instant the server sent, printed as a clock time in the CHANNEL's zone.
 *
 * <p>The zone is the restaurant's, never the visitor's: "back at 18:00" means
 * six in the evening where the kitchen is, and a diner reading the page from
 * another country is not being told to turn up at a different hour.
 */
export function timeInZone(
  instant: string | null,
  timeZone: string,
  locale: string,
): string | null {
  if (!instant) return null;
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      timeZone: timeZone || undefined,
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------ the services */

export interface ServiceOption {
  /** Stable value for the `<select>`; `''` is "now". */
  value: string;
  /** `HH:mm` in the channel's zone, or null for "now". */
  opensAt: string | null;
  closesAt: string | null;
  /** The ISO instant to pass as `at`, or null for "now". */
  at: string | null;
}

/**
 * The services a diner may preview, built from the site's own hours.
 *
 * <p>From `RestaurantSiteDto.hours` for today in the channel's zone — never
 * from a hard-coded list of meal names, because "lunch" and "öğle" and
 * "brunch" are the operator's business and half of them do not exist. Each
 * option is an `at` to hand back to `menu()`; the server decides what is on at
 * that moment.
 *
 * <p>Whether a service has already finished is not filtered here. Somebody
 * reading the page at 10am wants to see the lunch menu, and somebody reading it
 * at midnight is entitled to see what tomorrow's lunch looks like — the instant
 * is real either way and the server answers honestly for it.
 */
export function serviceOptions(site: RestaurantSite, now: Date = new Date()): ServiceOption[] {
  const zone = site.timeZone;
  const date = todayInZone(zone, now);
  const day = dayInZone(zone, now);
  if (!date || !day) return [];
  const options: ServiceOption[] = [];
  const seen = new Set<string>();
  for (const row of site.hours) {
    if (row.dayOfWeek !== day || !row.opensAt) continue;
    const at = zonedInstant(date, row.opensAt, zone);
    if (!at || seen.has(at)) continue;
    seen.add(at);
    options.push({
      value: at,
      opensAt: clock(row.opensAt),
      closesAt: clock(row.closesAt),
      at,
    });
  }
  return options.sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''));
}

/** `12:00:00` → `12:00`. Local duplicate of `shared.tsx`'s `clockTime` in
 *  string-in-string-out form, so this module stays free of anything React. */
function clock(value: string | null): string | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : value.trim();
}
